import { useEffect } from 'react';
import type { RefObject } from 'react';

/**
 * A pinça no modelo 3D — onda 316, Tarefa 316.6 (o celular; RN-251): dois dedos aproximam e afastam.
 *
 * O `Modelo3d` (o visual, um só no web e no Portal — 300.90) já gira com UM dedo (os eventos de ponteiro, com o
 * `touch-action: none` da caixa) e aproxima pela roda, pelos botões e pelo teclado, de 1,25 em 1,25. A pinça mora
 * aqui, na tela da campanha, sem mexer no visual: ouve os toques na FASE DE CAPTURA da caixa que embrulha o modelo e,
 * com dois dedos na tela do 3D,
 *
 *  · solta o arrasto do primeiro dedo (o `pointercancel` que o modelo já ouve), para a pinça não girar;
 *  · segura os movimentos (o modelo não os vê) e, a cada 1,25 de abertura ou de fechamento entre os dedos, manda à
 *    tela do 3D um passo da roda — o MESMO degrau do zoom do modelo, sem número novo;
 *  · até o último dedo sair, nenhum toque volta a girar.
 *
 * Só toque (`pointerType === 'touch'`): o mouse e a caneta seguem com o modelo.
 */

/** O degrau da pinça: o do zoom do `Modelo3d` (×1,25 — ESCOLHA DA TELA, SEM FONTE [esp.giro-passos]). */
const DEGRAU = 1.25;
/** O passo da roda que a pinça manda (o modelo só lê o sinal do `deltaY`). */
const RODA = 100;

export function usePincaDoModelo3d(caixa: RefObject<HTMLElement | null>, montado: boolean): void {
  useEffect(() => {
    const el = caixa.current;
    if (!el || !montado) return undefined;
    const dedos = new Map<number, { x: number; y: number }>();
    /** A distância entre os dedos no último degrau; null fora da pinça. */
    let base: number | null = null;
    const tela = (): HTMLElement | null => el.querySelector<HTMLElement>('.cptu-3d-tela');
    const distancia = (): number => {
      const [a, b] = [...dedos.values()];
      return Math.hypot(a.x - b.x, a.y - b.y);
    };
    const roda = (deltaY: number): void => {
      tela()?.dispatchEvent(new WheelEvent('wheel', { deltaY, bubbles: true, cancelable: true }));
    };

    // Só o toque de verdade: o `pointercancel` que a própria pinça manda não conta como dedo que saiu.
    const abaixar = (e: PointerEvent): void => {
      if (!e.isTrusted || e.pointerType !== 'touch' || !tela()?.contains(e.target as Node)) return;
      dedos.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (dedos.size === 2 && base === null) {
        base = distancia();
        // O primeiro dedo para de girar: o modelo solta o arrasto dele.
        const [primeiro] = dedos.keys();
        tela()?.dispatchEvent(new PointerEvent('pointercancel', { bubbles: true, pointerId: primeiro, pointerType: 'touch' }));
      }
      if (base !== null) e.stopPropagation();
    };
    const mover = (e: PointerEvent): void => {
      if (!e.isTrusted || !dedos.has(e.pointerId)) return;
      dedos.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (base === null) return;
      e.stopPropagation();
      if (dedos.size < 2 || base <= 0) return;
      const d = distancia();
      while (d / base >= DEGRAU) { roda(-RODA); base *= DEGRAU; }
      while (d / base <= 1 / DEGRAU) { roda(RODA); base /= DEGRAU; }
    };
    const soltar = (e: PointerEvent): void => {
      if (!e.isTrusted || !dedos.has(e.pointerId)) return;
      dedos.delete(e.pointerId);
      if (base === null) return;
      e.stopPropagation();
      if (dedos.size === 0) base = null;
    };

    el.addEventListener('pointerdown', abaixar, true);
    el.addEventListener('pointermove', mover, true);
    el.addEventListener('pointerup', soltar, true);
    el.addEventListener('pointercancel', soltar, true);
    return () => {
      el.removeEventListener('pointerdown', abaixar, true);
      el.removeEventListener('pointermove', mover, true);
      el.removeEventListener('pointerup', soltar, true);
      el.removeEventListener('pointercancel', soltar, true);
    };
  }, [caixa, montado]);
}
